import React, { useEffect, useState } from 'react';
import { api, ComplianceDocumentDto, UserManagementItem, PropertyDto } from '../../services/api';
import { Card, Btn, Alert, Spinner, C } from '../../components/UI';

// ── Compliance Documents (core primitive) ──────────────────────────────────────
// Generic holder-agnostic license/insurance/registration tracking — see ComplianceDocument's
// doc comment in Models.cs. This panel leads with "what's expiring soon" (the killer feature —
// nothing today surfaces this across contractors/vendors/properties at once) plus basic
// per-holder lookup and document creation.

const HOLDER_TYPES = ['Tenant', 'Contractor', 'Vendor', 'Property'] as const;
type HolderType = typeof HOLDER_TYPES[number];

const statusColors: Record<string, [string, string]> = {
    Approved: [C.greenBg, C.greenMid],
    Submitted: ['#DBEAFE', '#1D4ED8'],
    Pending: ['#F5F5F5', C.textMuted],
    Rejected: ['#FFF0F0', '#9B1C1C'],
    Expired: ['#FEF3C7', '#92400E'],
};

export function ComplianceDocumentPanel() {
    const [tab, setTab] = useState<'expiring' | 'lookup' | 'new'>('expiring');

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Compliance Documents</h2>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Licenses, insurance, and registrations — for contractors, vendors, and properties</div>
            </div>

            <div style={{ display: 'flex', gap: 2, marginBottom: 20, borderBottom: `2px solid ${C.border}` }}>
                {([['expiring', 'Expiring Soon'], ['lookup', 'Look up by holder'], ['new', '+ Add document']] as const).map(([key, label]) => (
                    <button key={key} onClick={() => setTab(key)} style={{
                        padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600,
                        color: tab === key ? C.greenMid : C.textSub,
                        borderBottom: tab === key ? `2px solid ${C.greenMid}` : '2px solid transparent',
                        marginBottom: -2,
                    }}>{label}</button>
                ))}
            </div>

            {tab === 'expiring' && <ExpiringSoonTab />}
            {tab === 'lookup' && <LookupTab />}
            {tab === 'new' && <NewDocumentTab onCreated={() => setTab('lookup')} />}
        </div>
    );
}

function DocumentRow({ d }: { d: ComplianceDocumentDto }) {
    const [bg, fg] = statusColors[d.status] || statusColors.Pending;
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 18px' }}>
            <span style={{
                fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
                background: d.isExpired ? statusColors.Expired[0] : bg,
                color: d.isExpired ? statusColors.Expired[1] : fg,
                flexShrink: 0, minWidth: 78, textAlign: 'center',
            }}>{d.isExpired ? 'Expired' : d.status}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{d.documentType}</div>
                <div style={{ fontSize: 12, color: C.textSub }}>{d.holderType} · {d.holderId.slice(0, 8)}…</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: d.isExpiringSoon || d.isExpired ? '#9B1C1C' : C.textMuted, fontWeight: d.isExpiringSoon || d.isExpired ? 700 : 400 }}>
                    {d.expiryDate ? `Expires ${new Date(d.expiryDate).toLocaleDateString()}` : 'No expiry'}
                </div>
                <a href={d.documentUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: C.greenMid }}>View document</a>
            </div>
        </div>
    );
}

function ExpiringSoonTab() {
    const [docs, setDocs] = useState<ComplianceDocumentDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [withinDays, setWithinDays] = useState(30);

    const load = () => {
        setLoading(true); setError('');
        api.getExpiringComplianceDocuments(withinDays)
            .then(setDocs)
            .catch(e => setError(e.message || 'Failed to load.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [withinDays]);

    return (
        <div>
            <Alert message={error} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: C.textSub }}>Within</label>
                <select value={withinDays} onChange={e => setWithinDays(Number(e.target.value))}
                    style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: '#fff' }}>
                    {[7, 30, 60, 90].map(d => <option key={d} value={d}>{d} days</option>)}
                </select>
                <Btn variant="outline" onClick={load}>Refresh</Btn>
            </div>
            {loading ? <Spinner /> : docs.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48, color: C.textMuted }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontWeight: 600, color: C.textSub, fontSize: 16 }}>Nothing expiring</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>No compliance documents due within {withinDays} days.</div>
                </Card>
            ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {docs.map((d, i) => <div key={d.id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.border}` }}><DocumentRow d={d} /></div>)}
                </Card>
            )}
        </div>
    );
}

// Shared by both the lookup and create tabs — picks a real Contractor/Vendor/Property by name
// instead of asking anyone to know a raw database id. Tenant has nothing to pick (the tenant
// itself is the holder), so it renders nothing and the caller should treat holderId as unused.
function HolderPicker({ holderType, holderId, onChange }: {
    holderType: HolderType; holderId: string; onChange: (id: string) => void;
}) {
    const [contractors, setContractors] = useState<UserManagementItem[]>([]);
    const [vendors, setVendors] = useState<UserManagementItem[]>([]);
    const [properties, setProperties] = useState<PropertyDto[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (holderType === 'Contractor' && contractors.length === 0) {
            setLoading(true);
            api.adminGetUserContractors().then(setContractors).catch(() => {}).finally(() => setLoading(false));
        } else if (holderType === 'Vendor' && vendors.length === 0) {
            setLoading(true);
            api.adminGetUserVendors().then(setVendors).catch(() => {}).finally(() => setLoading(false));
        } else if (holderType === 'Property' && properties.length === 0) {
            setLoading(true);
            api.getProperties().then(setProperties).catch(() => {}).finally(() => setLoading(false));
        }
    }, [holderType]); // intentionally omits contractors/vendors/properties — this only fetches each list once, on first switch to that holder type

    if (holderType === 'Tenant') {
        return <div style={{ fontSize: 13, color: C.textMuted, padding: '9px 0' }}>Your company — no selection needed.</div>;
    }

    const options = holderType === 'Contractor' ? contractors.map(c => ({ id: c.id, label: c.name }))
        : holderType === 'Vendor' ? vendors.map(v => ({ id: v.id, label: v.name }))
        : properties.map(p => ({ id: p.id, label: p.address }));

    return (
        <select value={holderId} onChange={e => onChange(e.target.value)} disabled={loading}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff', boxSizing: 'border-box' }}>
            <option value="">{loading ? 'Loading…' : `— Select a ${holderType.toLowerCase()} —`}</option>
            {options.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
    );
}

function LookupTab() {
    const [holderType, setHolderType] = useState<HolderType>('Contractor');
    const [holderId, setHolderId] = useState('');
    const [docs, setDocs] = useState<ComplianceDocumentDto[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const lookup = async () => {
        if (holderType !== 'Tenant' && !holderId) { setError('Select a holder.'); return; }
        setLoading(true); setError('');
        try { setDocs(await api.getComplianceDocumentsForHolder(holderType, holderId)); }
        catch (e: any) { setError(e.message || 'Failed to load.'); }
        finally { setLoading(false); }
    };

    return (
        <div>
            <Alert message={error} />
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <select value={holderType} onChange={e => { setHolderType(e.target.value as HolderType); setHolderId(''); }}
                    style={{ padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff' }}>
                    {HOLDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <div style={{ flex: 1 }}>
                    <HolderPicker holderType={holderType} holderId={holderId} onChange={setHolderId} />
                </div>
                <Btn onClick={lookup} disabled={loading}>{loading ? 'Looking up…' : 'Look up'}</Btn>
            </div>
            {docs && (docs.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 32, color: C.textMuted }}>No documents on file for this holder.</Card>
            ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    {docs.map((d, i) => <div key={d.id} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.border}` }}><DocumentRow d={d} /></div>)}
                </Card>
            ))}
        </div>
    );
}

// Reads a File into the base64 payload the backend expects (data-URL prefix stripped server-side).
function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error('Could not read that file.'));
        reader.readAsDataURL(file);
    });
}

function NewDocumentTab({ onCreated }: { onCreated: () => void }) {
    const [holderType, setHolderType] = useState<HolderType>('Contractor');
    const [holderId, setHolderId] = useState('');
    const [documentType, setDocumentType] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [issuedDate, setIssuedDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const create = async () => {
        if ((holderType !== 'Tenant' && !holderId) || !documentType.trim() || !file) {
            setError('Holder, document type, and a file are required.');
            return;
        }
        setSaving(true); setError('');
        try {
            const fileBase64 = await readFileAsBase64(file);
            await api.createComplianceDocument({
                holderType, holderId: holderType === 'Tenant' ? undefined : holderId,
                documentType: documentType.trim(),
                fileBase64, fileName: file.name,
                issuedDate: issuedDate || null, expiryDate: expiryDate || null,
                notes: notes.trim() || null,
            });
            setHolderId(''); setDocumentType(''); setFile(null); setIssuedDate(''); setExpiryDate(''); setNotes('');
            onCreated();
        } catch (e: any) {
            setError(e.message || 'Failed to create document.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card style={{ maxWidth: 520, padding: 24 }}>
            <Alert message={error} />
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Holder type</label>
                    <select value={holderType} onChange={e => { setHolderType(e.target.value as HolderType); setHolderId(''); }}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, background: '#fff', boxSizing: 'border-box' }}>
                        {HOLDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Holder</label>
                    <HolderPicker holderType={holderType} holderId={holderId} onChange={setHolderId} />
                </div>
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Document type</label>
                <input value={documentType} onChange={e => setDocumentType(e.target.value)} placeholder="e.g. Liability Insurance, Business License, Vacant Property Registration"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Document file</label>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files?.[0] ?? null)}
                    style={{ width: '100%', fontSize: 13, color: C.text }} />
                {file && <div style={{ fontSize: 12, color: C.textSub, marginTop: 4 }}>{file.name}</div>}
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Issued</label>
                    <input type="date" value={issuedDate} onChange={e => setIssuedDate(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 6 }}>Expires</label>
                    <input type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, color: C.text, boxSizing: 'border-box' }} />
                </div>
            </div>
            <Btn onClick={create} disabled={saving}>{saving ? 'Adding…' : 'Add document'}</Btn>
        </Card>
    );
}
